import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Settings as SettingsIcon, Briefcase, DollarSign, Users, MapPin, Shield, UserCog, Database } from 'lucide-react';

// Import existing manager components
import SettingsManager from './SettingsManager';
import ServicesManagement from './ServicesManagement';
import MerchantFeeManager from './MerchantFeeManager';
import AccountExecutiveManager from './AccountExecutiveManager';
import StateServiceDistributionManager from './settings/StateServiceDistributionManager';
import ApprovalThresholdsManager from './ApprovalThresholdsManager';
import RoleHierarchyManager from '../components/settings/RoleHierarchyManager';
import AccountCodeSettings from './AccountCodeSettings';

export default function UnifiedSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'general');

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  const tabs = [
    { id: 'general', name: 'General Settings', icon: SettingsIcon },
    { id: 'services', name: 'Services Management', icon: Briefcase },
    { id: 'merchant-fees', name: 'Merchant Fees', icon: DollarSign },
    { id: 'executives', name: 'Account Executives', icon: Users },
    { id: 'state-distributions', name: 'State-Service Distributions', icon: MapPin },
    { id: 'approval-thresholds', name: 'Approval Thresholds', icon: Shield },
    { id: 'roles', name: 'Role Hierarchy', icon: UserCog },
    { id: 'account-codes', name: 'Account Codes', icon: Database },
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header with subtle animation */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
              <SettingsIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Settings</h1>
              <p className="text-slate-600 mt-0.5 text-sm">Manage your application settings and configurations</p>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Tabs with hover effects */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`
                  flex items-center gap-2 px-6 py-4 border-b-3 transition-all duration-300 whitespace-nowrap
                  relative group
                  ${
                    activeTab === tab.id
                      ? 'border-indigo-600 text-indigo-600 font-semibold bg-indigo-50'
                      : 'border-transparent text-slate-600 hover:text-indigo-600 hover:bg-slate-50'
                  }
                `}
              >
                <tab.icon className={`w-5 h-5 transition-transform duration-300 ${activeTab === tab.id ? 'scale-110' : 'group-hover:scale-110'}`} />
                <span className="relative">
                  {tab.name}
                  {activeTab !== tab.id && (
                    <span className="absolute inset-x-0 -bottom-1 h-0.5 bg-indigo-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content with fade-in animation */}
      <div className="max-w-7xl mx-auto px-6 py-8 animate-fadeIn">
        {activeTab === 'general' && <SettingsManager />}
        {activeTab === 'services' && <ServicesManagement />}
        {activeTab === 'merchant-fees' && <MerchantFeeManager />}
        {activeTab === 'executives' && <AccountExecutiveManager />}
        {activeTab === 'state-distributions' && <StateServiceDistributionManager />}
        {activeTab === 'approval-thresholds' && <ApprovalThresholdsManager />}
        {activeTab === 'roles' && <RoleHierarchyManager />}
        {activeTab === 'account-codes' && <AccountCodeSettings />}
      </div>
    </div>
  );
}
